import React, { useState, useEffect } from 'react';
import { Row, Col, Nav, Tab } from 'react-bootstrap';
import {
  faDatabase,
  faFileAlt,
  faArrows,
  faSitemap
} from '@fortawesome/free-solid-svg-icons';
import {
  UilDatabase,
  UilFileAlt,
  UilSearch,
  UilGlobe
} from '@iconscout/react-unicons';
import HVASpinner from 'components/base/HVASpinner';
import KnowledgeCard from 'components/cards/KnowledgeCard';
import CollectionsManager from './CollectionsManager';
import DocumentsManager from './DocumentsManager';
import SearchPlayground from './KnowledgeSearchPlayground';
import WebSearchPlayground from './WebSearchPlayground';
import SystemStats from './SystemStats';
import {
  fetchSystemStats,
  KnowledgeHubStats
} from '../../services/knowledgehub.service';
import HVAToast from 'components/common/HVAToast';

const KnowledgeHubDashboard: React.FC = () => {
  const [activeKey, setActiveKey] = useState('collections');
  const [stats, setStats] = useState<KnowledgeHubStats | null>(null);
  const [loading, setLoading] = useState(true);
  // Toast states
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState<
    'success' | 'error' | 'warning' | 'info'
  >('info');

  useEffect(() => {
    loadStats();
  }, []);

  // Helper function to show toast
  const showToastMessage = (
    message: string,
    variant: 'success' | 'error' | 'warning' | 'info'
  ) => {
    setToastMessage(message);
    setToastVariant(variant);
    setShowToast(true);
  };

  const loadStats = async () => {
    try {
      setLoading(true);

      const data = await fetchSystemStats();

      setStats(data);
    } catch (err) {
      showToastMessage('Error loading system statistics', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Toast Notification */}
      <HVAToast
        show={showToast}
        onClose={() => setShowToast(false)}
        message={toastMessage}
        variant={toastVariant}
      />
      <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 pt-0 pb-2 mb-0 bg-body">
        <Row className="mb-4">
          <Col>
            <h2 className="text-body-emphasis fw-bolder mb-2">
              Knowledge Hub Management
            </h2>
            <p className="text-body-secondary">
              Manage your knowledge hub collections and documents. Test
              Knowledge and Web Search capabilities.
            </p>
          </Col>
        </Row>
        {/* Statistics Cards */}
        {loading ? (
          <div className="pb-1 text-center">
            <HVASpinner />
          </div>
        ) : (
          stats && (
            <Row className="mb-2 g-3">
              <Col xs={12} sm={6} md={3}>
                <KnowledgeCard
                  icon={faDatabase}
                  color="primary"
                  label="Total"
                  title="Collections"
                  value={stats.totalCollections}
                  data={[]} // No se usa en el componente actual
                />
              </Col>
              <Col xs={12} sm={6} md={3}>
                <KnowledgeCard
                  icon={faFileAlt}
                  color="primary"
                  label="Total"
                  title="Documents"
                  value={stats.totalDocuments}
                  data={[]}
                />
              </Col>
              <Col xs={12} sm={6} md={3}>
                <KnowledgeCard
                  icon={faArrows}
                  color="primary"
                  label="Total"
                  title="Vectors"
                  value={stats.totalVectors}
                  formatValue={true}
                  data={[]}
                />
              </Col>
              <Col xs={12} sm={6} md={3}>
                <KnowledgeCard
                  icon={faSitemap}
                  color="primary"
                  label="Average"
                  title="Vectors/Doc"
                  value={
                    stats.totalDocuments > 0
                      ? Math.round(stats.totalVectors / stats.totalDocuments)
                      : 0
                  }
                  data={[]}
                />
              </Col>
            </Row>
          )
        )}
        {/* Statistics Charts */}
        <Row className="mb-2">
          <Col>
            <SystemStats stats={stats} isVisible={true} />
          </Col>
        </Row>
      </div>
      <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 pt-0 pb-5 mb-0 bg-body-emphasis border-top">
        {/* Main Content Tabs */}
        <div className="mb-4">
          <div>
            <Tab.Container activeKey={activeKey}>
              <Nav variant="underline" className="pt-2 mb-4">
                <Nav.Item>
                  <Nav.Link
                    eventKey="collections"
                    active={activeKey === 'collections'}
                    onClick={() => setActiveKey('collections')}
                  >
                    <UilDatabase size={18} className="me-2" />
                    Collections
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link
                    eventKey="documents"
                    active={activeKey === 'documents'}
                    onClick={() => setActiveKey('documents')}
                  >
                    <UilFileAlt size={18} className="me-2" />
                    Documents
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link
                    eventKey="search"
                    active={activeKey === 'search'}
                    onClick={() => setActiveKey('search')}
                  >
                    <UilSearch size={18} className="me-2" />
                    Knowledge Search Playground
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link
                    eventKey="websearch"
                    active={activeKey === 'websearch'}
                    onClick={() => setActiveKey('websearch')}
                  >
                    <UilGlobe size={18} className="me-2" />
                    Web Search Playground
                  </Nav.Link>
                </Nav.Item>
              </Nav>
              <Tab.Content>
                <Tab.Pane
                  eventKey="collections"
                  active={activeKey === 'collections'}
                >
                  <CollectionsManager onUpdate={loadStats} />
                </Tab.Pane>
                <Tab.Pane
                  eventKey="documents"
                  active={activeKey === 'documents'}
                >
                  <DocumentsManager onUpdate={loadStats} />
                </Tab.Pane>
                <Tab.Pane eventKey="search" active={activeKey === 'search'}>
                  <SearchPlayground />
                </Tab.Pane>
                <Tab.Pane
                  eventKey="websearch"
                  active={activeKey === 'websearch'}
                >
                  <WebSearchPlayground />
                </Tab.Pane>
              </Tab.Content>
            </Tab.Container>
          </div>
        </div>
      </div>
    </>
  );
};

export default KnowledgeHubDashboard;
